import type { OrchestratorSchemas } from '@ctcells/contract'
import { REQUEST_TIMEOUT_MS } from '@ctcells/core'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { ApiError } from '../errors.ts'
import { keys } from '../keys.ts'

/**
 * 한 번에 풀기 — `POST /safety/stops/release`(`IFC-RO-110` · v0.9.4 · 2026-10-08 신설).
 *
 * 실물(`ctcells-core/app/views/stops.py` `release_stops` · 2026-10-11)
 *
 * | 칸 | 값 |
 * | --- | --- |
 * | 본문 | `StopsReleaseRequest{command_id, reason(필수 · 빈 문자열 422), ids, physical_checked?}` |
 * | 헤더 | `X-Control-Token` **필수**(`require_control_lock` · `controlToken.ts` 표) |
 * | 역할 | operator · admin(`WRITE_ROLES`) |
 * | 200 | `StopsReleaseResult{items: {id, done, error_code?, said}[]}` — 항목마다 결과. **일부 실패도 200** 이다 |
 * | 409 | `STOPS_CHANGED`(`context.added`·`removed`) — 사람이 본 목록과 지금 목록이 다르다 · **아무것도 안 풀었다** |
 * | 422 | `REQUEST_VALIDATION_FAIL` · `context.reason=physical_check_required` — 종류별 현장 확인 누락(context.checks · ids), 아무것도 풀지 않았다 |
 *
 * 🔴 **`ids` 는 «화면이 보인 풀 항목 전부»다** — 서버가 `releasable=true ∧ parent=null` 집합과 **같은지** 본다.
 *    골라 풀기가 아니다. 부분집합을 보내면 409 다(`releasableStopIds` 가 그 집합을 짓는다).
 * 🔴 **배치 재개를 하지 않는다** — 사람이 [재개]를 따로 누른다(`SM-BATCH-02`).
 * 🔴 **재시도에 `commandId` 를 새로 만들지 마라** — 같은 값이면 서버가 첫 답을 그대로 준다(`IFC-COM-03`).
 */
export type StopsReleaseResult = OrchestratorSchemas['schemas']['StopsReleaseResult']

export interface ReleaseStopsVars {
  /** 화면이 보인 «풀 항목» id 전부 */
  ids: readonly string[]
  /** 🔴 감사 원장에 봉인된다 — 사람이 적는다. 비우지 않는다 */
  reason: string
  /** 사람이 현장에서 로봇·트레이·문을 직접 확인했다(체크 값 그대로) */
  physicalChecked: boolean
  /**
   * 풀 항목 종류마다 확인한 것(계약 `StopsReleaseRequest.physical_checks`) — 빠지면 422 `physical_check_required`.
   * 🔴 어휘는 서버가 정한 짝 그대로다(`requiredStopChecks`). 안 넘기면 키를 싣지 않는다(종전 호출 그대로).
   */
  physicalChecks?: readonly StopPhysicalCheck[] | undefined
  commandId?: string
}

export type StopPhysicalCheck = NonNullable<
  OrchestratorSchemas['schemas']['StopsReleaseRequest']['physical_checks']
>[number]

/**
 * 풀 항목 종류 → 사람이 현장에서 볼 것(계약 `StopsReleaseRequest.physical_checks` 설명의 짝 그대로 · 백엔드 0.28.15).
 * door_open → door_area · robot_holding · robot_pose → robot_area · quarantine → tray_positions. 짝이 없는 종류(safety · module_hold)는 확인이 필요 없다.
 */
const STOP_KIND_CHECK: Readonly<Record<string, StopPhysicalCheck>> = {
  door_open: 'door_area',
  robot_holding: 'robot_area',
  robot_pose: 'robot_area',
  quarantine: 'tray_positions',
}

/** 그 «풀 항목»들이 요구하는 현장 확인 — 중복 없이 서버 어휘로 */
export function requiredStopChecks(
  items: readonly Pick<OrchestratorSchemas['schemas']['StopItem'], 'id' | 'kind'>[],
  ids: readonly string[],
): StopPhysicalCheck[] {
  const wanted = new Set(ids)
  const out = new Set<StopPhysicalCheck>()
  for (const item of items) {
    const check = STOP_KIND_CHECK[item.kind]
    if (wanted.has(item.id) && check !== undefined) out.add(check)
  }
  return [...out]
}

/**
 * «풀 항목» id — 서버 `services/stops.releasable_ids` 와 같은 규칙(`releasable ∧ parent 없음`).
 * 🔴 하위 줄(`parent` 가 있는 `module_hold`)은 상위(안전 해제)가 함께 풀어 세지 않는다 — 넣으면 409 다.
 */
export function releasableStopIds(
  items: readonly OrchestratorSchemas['schemas']['StopItem'][],
): string[] {
  return items
    .filter((item) => item.releasable && (item.parent === undefined || item.parent === null))
    .map((item) => item.id)
}

export const useReleaseStops = createCommand<ReleaseStopsVars, StopsReleaseResult>({
  send: ({ ids, reason, physicalChecked, physicalChecks, commandId }) =>
    orchestrator.request<StopsReleaseResult>({
      method: 'POST',
      path: '/safety/stops/release',
      body: {
        command_id: commandId ?? newCommandId(),
        reason,
        ids: [...ids],
        physical_checked: physicalChecked,
        ...(physicalChecks === undefined || physicalChecks.length === 0
          ? {}
          : { physical_checks: [...physicalChecks] }),
      },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  /*
   * 풀기는 안전 상태 · 모듈(재개 가능) · 멈춘 배치(`notify_halted`)를 바꾼다. 목록(`keys.safety.stops()`)은
   * `keys.safety.all()` 접두로 같이 빠진다 — 낙관적 갱신 없이 서버에 다시 묻는다(DP-04).
   */
  invalidates: () => [keys.safety.all(), keys.modules.all(), keys.batches.all()],
})

/** 422의 서버 요구를 그대로 보존한다. 새 어휘도 버리지 않고 화면에 남겨야 한다. */
export function stopsReleaseFailure(
  error: unknown,
): { kind: 'changed' } | { kind: 'physical'; checks: string[]; ids: string[] } | { kind: 'other' } {
  if (!(error instanceof ApiError)) return { kind: 'other' }
  if (error.status === 409 && error.error_code === 'STOPS_CHANGED') return { kind: 'changed' }
  if (error.status === 422 && error.context?.['reason'] === 'physical_check_required') {
    const strings = (value: unknown): string[] =>
      Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
    return {
      kind: 'physical',
      checks: strings(error.context['checks']),
      ids: strings(error.context['ids']),
    }
  }
  return { kind: 'other' }
}

export function isStopPhysicalCheck(value: string): value is StopPhysicalCheck {
  return ['door_area', 'robot_area', 'tray_positions', 'rotor_balanced'].includes(value)
}
